import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Link as MuiLink,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { billingApi } from '../services/billingApi';
import type { AuditEventDto, ClaimDetailDto, ClaimPayloadInsurance, DriveTraceEntry } from '../types/billing';
import StatusChip from './StatusChip';
import WarningChips from './WarningChips';
import AutoCreatedChip from './AutoCreatedChip';

export interface ClaimDrawerProps {
  claimId: string | null;
  onClose: () => void;
}

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field: React.FC<FieldProps> = ({ label, value }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
      {label}
    </Typography>
    <Typography variant="body2">{value ?? '-'}</Typography>
  </Box>
);

const SectionHeader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="overline" color="text.secondary">
    {children}
  </Typography>
);

/** Compact "✓ step  850ms" / "✗ step - reason  3000ms" list. Right-aligned
 *  duration so the eye can sweep it like a profile and spot the slow
 *  slot. No collapse for now: ICD traces top out at 12 entries, line
 *  items at 12, save + scalars are each one - well under the threshold
 *  where a click-to-expand pays for itself. */
const DriveTraceList: React.FC<{ entries: DriveTraceEntry[] }> = ({ entries }) => {
  const totalMs = entries.reduce((acc, e) => acc + (e.durationMs ?? 0), 0);
  return (
    <Box sx={{ mt: 0.5 }}>
      {entries.map((e, idx) => {
        const mark = e.outcome === 'ok' ? '✓' : e.outcome === 'failed' ? '✗' : '·';
        const color =
          e.outcome === 'ok' ? 'success.main'
          : e.outcome === 'failed' ? 'error.main'
          : 'text.secondary';
        return (
          <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.25, alignItems: 'baseline' }}>
            <Typography variant="body2" sx={{ color, minWidth: 16, fontFamily: 'monospace' }}>
              {mark}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
              <Box component="span" sx={{ fontFamily: 'monospace' }}>{e.step}</Box>
              {e.detail && (
                <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                  - {e.detail}
                </Box>
              )}
            </Typography>
            {typeof e.durationMs === 'number' && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', fontFamily: 'monospace', minWidth: 48, textAlign: 'right' }}
              >
                {formatMs(e.durationMs)}
              </Typography>
            )}
          </Box>
        );
      })}
      {totalMs > 0 && (
        <Box sx={{ display: 'flex', gap: 1, mt: 0.5, pt: 0.5, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" sx={{ flex: 1, color: 'text.secondary', fontFamily: 'monospace' }}>
            total
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace', minWidth: 48, textAlign: 'right' }}>
            {formatMs(totalMs)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

/** Compact ms label - "850 ms" / "3.2 s" depending on magnitude so a
 *  multi-second total doesn't read as "12345 ms" in tiny mono. */
function formatMs(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(ms < 10_000 ? 2 : 1)} s`;
}

function safeParse(json: string | null): Record<string, unknown> {
  if (json === null) return {};
  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function formatHHmm(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function renderAuditRow(e: AuditEventDto): React.ReactNode {
  const payload = safeParse(e.payload);
  const actorName = e.actorName ?? e.actorEmail ?? 'unknown';
  const ts = formatHHmm(e.createdAt);

  switch (e.action) {
    case 'DISPATCH': {
      const verb = payload.forced === true ? 'retried' : 'dispatched';
      return (
        <Box key={e.id} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
            {ts}
          </Typography>
          <Typography variant="body2">
            <b>{actorName}</b> {verb}
          </Typography>
        </Box>
      );
    }
    case 'JOB_PICKED_UP': {
      const ext = String(payload.extension ?? 'unknown');
      return (
        <Box key={e.id} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
            {ts}
          </Typography>
          <Typography variant="body2">
            Extension <code>{ext}</code> picked up
          </Typography>
        </Box>
      );
    }
    case 'OA_DRAFT_CREATED': {
      return (
        <Box key={e.id} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
            {ts}
          </Typography>
          <Typography variant="body2" sx={{ color: 'success.main' }}>
            OA draft created · patient <code>{String(payload.oaPatientId ?? '')}</code> ·{' '}
            visit <code>{String(payload.oaVisitId ?? '')}</code>
          </Typography>
        </Box>
      );
    }
    case 'JOB_FAILED': {
      const err = String(payload.error ?? 'unknown error');
      const screenshotKey = payload.screenshotKey;
      const hasScreenshot =
        typeof screenshotKey === 'string'
        && screenshotKey !== 'null'
        && screenshotKey.length > 0;
      // For ACTION_JOB_FAILED, target_type='JOB' and target_id is the jobId
      // (see BillingJobService.postResult). Fetch the presigned URL via JSON -
      // the controller used to 302-redirect, but anchor navigation cannot
      // carry the Bearer JWT so we route through the API client instead.
      const targetJobId = e.targetId ?? '';
      const openScreenshot = async () => {
        try {
          const { url } = await billingApi.getJobScreenshotUrl(targetJobId);
          window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
          // Best-effort - non-blocking on failure. Could surface a snackbar
          // in a followup.
        }
      };
      return (
        <Box key={e.id} sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
            {ts}
          </Typography>
          <Typography variant="body2" sx={{ color: 'error.main' }}>
            Failed - {err}
            {hasScreenshot && (
              <>
                {' · '}
                <MuiLink
                  component="button"
                  type="button"
                  onClick={openScreenshot}
                  underline="hover"
                  sx={{ verticalAlign: 'baseline' }}
                >
                  screenshot
                </MuiLink>
              </>
            )}
          </Typography>
        </Box>
      );
    }
    default:
      return null;
  }
}

function renderInsurance(ins: ClaimPayloadInsurance | null): React.ReactNode {
  if (ins === null) {
    return <Typography variant="body2">(none on file)</Typography>;
  }
  return (
    <Box>
      <Field label="Raw Carrier" value={ins.rawCarrier} />
      <Field label="Normalized Carrier" value={ins.normalizedCarrier} />
      <Field label="Plan" value={ins.plan} />
      <Field label="Group ID" value={ins.groupId} />
      <Field label="Member ID" value={ins.memberId} />
      <Field label="Copay" value={ins.copay} />
      <Field label="Deductible" value={ins.deductible} />
      <Field label="Insured First Name" value={ins.insuredFirstName} />
      <Field label="Insured Last Name" value={ins.insuredLastName} />
      <Field label="Rel. to Insured" value={ins.relToInsured} />
    </Box>
  );
}

/** Claim statuses where an operator can mark a claim as manually completed.
 *  Excludes DISPATCHED (BETHY is mid-flight), OA_DRAFT_CREATED / SUBMITTED
 *  (terminal in OA), and MANUALLY_COMPLETED itself (handled by un-mark). */
const ACTIONABLE_STATUSES = new Set(['STAGED', 'FAILED', 'NEEDS_REVIEW', 'SKIPPED']);

const ClaimDrawer: React.FC<ClaimDrawerProps> = ({ claimId, onClose }) => {
  const [detail, setDetail] = useState<ClaimDetailDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditEvents, setAuditEvents] = useState<AuditEventDto[] | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [markOpen, setMarkOpen] = useState(false);
  const [unmarkOpen, setUnmarkOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);

  const submitMark = async () => {
    if (!detail) return;
    setMarking(true);
    setMarkError(null);
    try {
      const updated = await billingApi.markComplete(detail.id, detail.practiceId, noteText);
      setDetail(updated);
      setMarkOpen(false);
      setNoteText('');
    } catch (e) {
      setMarkError(e instanceof Error ? e.message : 'Failed to mark claim complete');
    } finally {
      setMarking(false);
    }
  };
  const submitUnmark = async () => {
    if (!detail) return;
    setMarking(true);
    setMarkError(null);
    try {
      const updated = await billingApi.unmarkComplete(detail.id, detail.practiceId);
      setDetail(updated);
      setUnmarkOpen(false);
    } catch (e) {
      setMarkError(e instanceof Error ? e.message : 'Failed to un-mark claim');
    } finally {
      setMarking(false);
    }
  };

  useEffect(() => {
    if (claimId === null) {
      setDetail(null);
      setError(null);
      setLoading(false);
      setAuditEvents(null);
      setAuditLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setDetail(null);
    setAuditEvents(null);
    setAuditLoading(true);
    billingApi
      .getClaim(claimId)
      .then((d) => {
        if (!cancelled) {
          setDetail(d);
          setLoading(false);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load claim');
          setLoading(false);
        }
      });
    billingApi
      .listClaimAudit(claimId)
      .then((events) => {
        if (!cancelled) setAuditEvents(events);
      })
      .catch(() => {
        if (!cancelled) setAuditEvents([]);
      })
      .finally(() => {
        if (!cancelled) setAuditLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [claimId]);

  const A_TO_L = 'ABCDEFGHIJKL';

  return (
    <Drawer
      anchor="right"
      variant="temporary"
      open={claimId !== null}
      onClose={onClose}
      sx={{ '& .MuiDrawer-paper': { width: 480 } }}
      PaperProps={{ role: 'dialog' }}
    >
      {claimId !== null && (
        <Box sx={{ p: 2 }}>
          {loading && (
            <Box>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} height={24} animation="wave" />
              ))}
            </Box>
          )}

          {error !== null && <Alert severity="error">{error}</Alert>}

          {detail !== null && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="h6">
                  {detail.payload.patient.firstName ?? ''} {detail.payload.patient.lastName ?? ''}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StatusChip status={detail.status} />
                  <AutoCreatedChip shown={detail.oaPatientAutoCreated} />
                  {ACTIONABLE_STATUSES.has(detail.status) && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setMarkError(null);
                        setMarkOpen(true);
                      }}
                    >
                      Mark manually completed
                    </Button>
                  )}
                  {detail.status === 'MANUALLY_COMPLETED' && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setMarkError(null);
                        setUnmarkOpen(true);
                      }}
                    >
                      Un-mark
                    </Button>
                  )}
                  <IconButton aria-label="close" onClick={onClose} size="small">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <SectionHeader>Patient</SectionHeader>
              <Field label="DOB" value={detail.payload.patient.dob} />
              <Field label="Sex" value={detail.payload.patient.sex} />
              <Field
                label="Phone"
                value={
                  detail.payload.patient.phone
                    ? `${detail.payload.patient.phone}${
                        detail.payload.patient.phoneType ? ` (${detail.payload.patient.phoneType})` : ''
                      }`
                    : null
                }
              />
              <Field
                label="Address"
                value={
                  <Box component="span" sx={{ display: 'block' }}>
                    <Box component="span" sx={{ display: 'block' }}>
                      {detail.payload.patient.address ?? '-'}
                    </Box>
                    {detail.payload.patient.suite && (
                      <Box component="span" sx={{ display: 'block' }}>
                        {detail.payload.patient.suite}
                      </Box>
                    )}
                    <Box component="span" sx={{ display: 'block' }}>
                      {[
                        detail.payload.patient.city,
                        detail.payload.patient.state,
                        detail.payload.patient.zip,
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    </Box>
                  </Box>
                }
              />
              <Field label="SSN" value={detail.payload.patient.ssn} />

              <Divider sx={{ my: 2 }} />

              <SectionHeader>Encounter</SectionHeader>
              <Field label="Date of Service" value={detail.payload.encounter.dateOfService} />
              <Field label="Provider" value={detail.payload.encounter.providerName} />
              <Field label="Service Location" value={detail.payload.encounter.serviceLocation} />
              <Field label="POS" value={detail.payload.encounter.pos} />
              <Field
                label="Referring Provider"
                value={
                  detail.payload.encounter.referringProvider
                    ? `${detail.payload.encounter.referringProvider}${
                        detail.payload.encounter.referringNpi
                          ? ` (NPI ${detail.payload.encounter.referringNpi})`
                          : ''
                      }`
                    : null
                }
              />
              <Field label="Supervising Provider" value={detail.payload.encounter.supervisingProvider} />
              <Field
                label="Ordering Provider"
                value={
                  detail.payload.encounter.orderingProvider
                    ? `${detail.payload.encounter.orderingProvider}${
                        detail.payload.encounter.orderingNpi
                          ? ` (NPI ${detail.payload.encounter.orderingNpi})`
                          : ''
                      }`
                    : null
                }
              />
              <Field label="Prior Auth" value={detail.payload.encounter.priorAuth} />

              <Divider sx={{ my: 2 }} />

              <SectionHeader>Primary Insurance</SectionHeader>
              {renderInsurance(detail.payload.primaryInsurance)}

              <Divider sx={{ my: 2 }} />

              <SectionHeader>Secondary Insurance</SectionHeader>
              {renderInsurance(detail.payload.secondaryInsurance)}

              <Divider sx={{ my: 2 }} />

              <SectionHeader>ICDs (A–L)</SectionHeader>
              {detail.payload.icdsAtoL.length === 0 ? (
                <Typography variant="body2">(none)</Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  {detail.payload.icdsAtoL.map((code, idx) => (
                    <Chip
                      key={`${code}-${idx}`}
                      label={`${A_TO_L[idx] ?? '?'}: ${code}`}
                      size="small"
                      variant="filled"
                    />
                  ))}
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <SectionHeader>Line Items</SectionHeader>
              {detail.payload.lineItems.length === 0 ? (
                <Typography variant="body2">(no line items)</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CPT</TableCell>
                      <TableCell>Modifiers</TableCell>
                      <TableCell>ICD Ptr</TableCell>
                      <TableCell>Units</TableCell>
                      <TableCell>Unit Charge</TableCell>
                      <TableCell>NDC</TableCell>
                      <TableCell>DOS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detail.payload.lineItems.map((li, idx) => (
                      <TableRow key={`${li.cpt}-${idx}`}>
                        <TableCell>{li.cpt}</TableCell>
                        <TableCell>{li.modifiers.join(', ')}</TableCell>
                        <TableCell>{li.icdPointer}</TableCell>
                        <TableCell>{li.units ?? '-'}</TableCell>
                        <TableCell>{li.unitCharge ?? '-'}</TableCell>
                        <TableCell>{li.ndc ?? '-'}</TableCell>
                        <TableCell>{li.dosFrom ?? '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}

              {detail.warnings.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SectionHeader>Warnings</SectionHeader>
                  <WarningChips codes={detail.warnings} max={999} />
                </>
              )}

              {detail.driveTrace && detail.driveTrace.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <SectionHeader>Bridge trace</SectionHeader>
                  <DriveTraceList entries={detail.driveTrace} />
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="caption" color="text.secondary">
                Created {detail.createdAt} · Updated {detail.updatedAt} · Source claim{' '}
                {detail.externalClaimId} · OA patient {detail.oaPatientId ?? '-'} · OA visit{' '}
                {detail.oaVisitId ?? '-'}
              </Typography>

              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <SectionHeader>Activity</SectionHeader>
                {auditLoading && (
                  <>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="70%" />
                    <Skeleton variant="text" width="85%" />
                  </>
                )}
                {!auditLoading && auditEvents !== null && auditEvents.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No activity yet - this claim hasn't been dispatched.
                  </Typography>
                )}
                {!auditLoading && auditEvents !== null && auditEvents.length > 0 && (
                  <Box>
                    {auditEvents
                      .filter((e) => e.action !== 'CROSSWALK_WRITE' && e.action !== 'UPLOAD')
                      .map((e) => renderAuditRow(e))}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Dialog
        open={markOpen}
        onClose={() => {
          setMarkOpen(false);
          setMarkError(null);
        }}
      >
        <DialogTitle>Mark claim as manually completed?</DialogTitle>
        <DialogContent>
          {markError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {markError}
            </Alert>
          )}
          <DialogContentText>
            This claim will be excluded from BETHY dispatch. Use when you've
            already entered it in Practice Mate by hand.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Note (optional)"
            multiline
            rows={2}
            fullWidth
            inputProps={{ maxLength: 200 }}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setMarkOpen(false);
              setMarkError(null);
            }}
            disabled={marking}
          >
            Cancel
          </Button>
          <Button onClick={submitMark} disabled={marking} variant="contained">
            Mark complete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unmarkOpen}
        onClose={() => {
          setUnmarkOpen(false);
          setMarkError(null);
        }}
      >
        <DialogTitle>Un-mark claim?</DialogTitle>
        <DialogContent>
          {markError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {markError}
            </Alert>
          )}
          <DialogContentText>
            Return claim to STAGED so BETHY can redispatch it.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setUnmarkOpen(false);
              setMarkError(null);
            }}
            disabled={marking}
          >
            Cancel
          </Button>
          <Button onClick={submitUnmark} disabled={marking} variant="contained">
            Un-mark
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default ClaimDrawer;
